// ADENDA z62(1) (Diego, 29-oct-2026, con botón: «los kWh que el vendedor escribe a mano en un mes SIN recibo salen como
// ESTIMADO en el PDF; solo los meses con recibo son reales»). Desde z61 la tabla de Revisión pone en blanco los meses del año que
// el recibo no trae y el vendedor los escribe (o usa «Sugerir con IA»). Lo sugerido ya iba «estimado»; lo ESCRITO A MANO se
// guardaba «manual» y el PDF lo contaba como recibo real («12 recibos» con 8 recibos y 4 meses tecleados). Aquí vive la regla
// única de qué es un mes «escrito a mano sin recibo»; la usan el PDF (mapear) y la pantalla (la tabla y su resumen).
//
// Lo que NO cambia: el MOTOR (sus cifras y sus reglas de aceptación son las de hoy: un mes escrito a mano sigue contando para los
// mínimos de meses reales). Es una etiqueta y un conteo de lo que dice el PDF y la pantalla.
//
// Qué SÍ es un mes con recibo (real): un renglón del recibo o de su historial impreso, aunque el vendedor le haya cambiado los kWh
// (z25: guarda lo que decía el recibo) y la captura a mano ENTERA (sin lectura: el vendedor copia el recibo que tiene en la mano).
import { vieneDelRecibo } from './ajuste-consumos.ts';
import type { Aviso } from './dominio/avisos.ts';
import type { ContenidoCotizacion } from './dominio/cotizacion.ts';
import { formatoPara } from './dominio/cuestionario.ts';
import type { PeriodoCalculo } from './dominio/opciones.ts';
import type { LecturaRecibo, PeriodoConsumo } from './dominio/recibo.ts';
import { mesDelRenglon } from './dominio/recibo.ts';

/** ¿La lectura vino de recibo(s)? (algún renglón del recibo o de su historial; una captura a mano desde cero no tiene ninguno). */
export const lecturaDeRecibos = (l: Pick<LecturaRecibo, 'historial'> | null | undefined): boolean => !!l && l.historial.some(vieneDelRecibo);

/** ¿Este renglón lo escribió el vendedor a mano en un mes SIN recibo? Origen «manual», que no viene de un recibo (tampoco ajustado de
 *  uno: `vieneDelRecibo`), en una lectura que SÍ vino de recibos. Un mes que proyectó la IA y luego cambió el vendedor también
 *  (sigue sin recibo). */
export function escritoSinRecibo(p: Pick<PeriodoConsumo, 'origen' | 'ajuste_vendedor'>, l: Pick<LecturaRecibo, 'historial'> | null | undefined): boolean {
  return p.origen === 'manual' && !vieneDelRecibo(p as PeriodoConsumo) && lecturaDeRecibos(l);
}

/** Los periodos del CÁLCULO (índices de `periodos`, los de un medidor) que salieron de renglones escritos a mano sin recibo. El
 *  motor los deja con origen «manual»; se reconocen por su corte (el renglón con el mismo fin) o, si el renglón no tiene fechas
 *  («ABR 26», el historial impreso de GDMT), por su mes. Si en ese corte hay también un renglón del recibo, cuenta como del recibo
 *  (lo conservador: no se le quita lo real a un periodo que sí tiene recibo). */
export function periodosEscritosSinRecibo(l: LecturaRecibo | null | undefined, periodos: readonly Pick<PeriodoCalculo, 'fin' | 'etiqueta' | 'origen'>[]): number[] {
  if (!l || !lecturaDeRecibos(l)) return [];
  return periodos.flatMap((p, i) => {
    if (p.origen !== 'manual') return [];
    let mismos = l.historial.filter((h) => !!h.fin && h.fin === p.fin);
    if (!mismos.length) {
      const mes = mesDelRenglon({ fin: p.fin, etiqueta: p.etiqueta });
      mismos = l.historial.filter((h) => !h.fin && mes !== null && mesDelRenglon(h) === mes);
    }
    return mismos.length && mismos.every((h) => escritoSinRecibo(h, l)) ? [i] : [];
  });
}

/** hoy85 vuelta 3: cuántos renglones escribió el vendedor a mano sin recibo en las lecturas de los DOS medidores (A y B). Con dos
 *  medidores el PDF los imprime como recibo (la plantilla no imprime meses estimados con dos medidores): esto es lo que lo hace
 *  VISIBLE (el recuadro de Revisión en la pestaña de cada medidor, el chip de Opciones y la nota interna del PDF). */
export function escritosSinReciboConDosMedidores(c: Pick<ContenidoCotizacion, 'lecturas'>): number {
  return (['A', 'B'] as const).reduce((n, M) => {
    const l = c.lecturas[M];
    return n + (l ? l.historial.filter((h) => escritoSinRecibo(h, l)).length : 0);
  }, 0);
}

/** La nota interna (para Diego, en el .avisos.txt del PDF) cuando los meses escritos a mano sí van «estimado». */
export const notaMesesEscritosEstimados = (n: number, bimestral: boolean): string =>
  `ADENDA z62(1): ${n} ${bimestral ? (n === 1 ? 'bimestre' : 'bimestres') : n === 1 ? 'mes' : 'meses'} que el vendedor escribió a mano (sin recibo) ${n === 1 ? 'va' : 'van'} como ESTIMADO en el PDF (barras punteadas): solo los meses con recibo son reales.`;

/** hoy85 (verificador del vendedor: «la pantalla promete lo que el PDF no cumple»): POR QUÉ el PDF imprime como RECIBO (como antes) los
 *  meses que el vendedor escribió a mano sin recibo, cuando marcarlos «estimado» no deja armar la propuesta. Es la MISMA regla que
 *  aplica el PDF (paquetes/pdf → mapearAPropuestaV1, que la consulta primero) y la que lee la pantalla (Revisión y Opciones) para no
 *  prometer «en el PDF va como estimado» cuando no va así. Las razones que se saben ANTES de armar el PDF:
 *   · dos medidores (la plantilla no imprime meses estimados con dos medidores);
 *   · la propuesta de baterías (híbrido): solo imprime meses de recibo;
 *   · con esos meses estimados quedan menos meses de recibo que los que pide la propuesta (3 meses o 2 bimestres; 1 en industria).
 *  Lo que solo se sabe al armarlo (una variante que la plantilla no tiene) lo sigue diciendo la nota interna del PDF.
 *  null = el PDF sí los marca «estimado», o no hay meses escritos a mano sin recibo. */
export function porqueEscritosVanComoRecibo(c: Pick<ContenidoCotizacion, 'cuestionario' | 'lecturas' | 'resultado'>): string | null {
  const r = c.resultado;
  if (!r) return null;
  const q = c.cuestionario;
  // hoy85 vuelta 3 (verificador del vendedor: «con dos medidores la pantalla sigue prometiendo lo que el PDF no cumple»): con dos
  // medidores el cálculo SUMA A y B por mes y el periodo sumado deja de ser «manual» (queda del recibo), así que no se reconoce en
  // `r.periodos`; se cuentan en las LECTURAS de los DOS medidores (antes solo el A: los meses escritos a mano en el B salían como
  // recibo en el PDF y la pantalla de B decía «en el PDF va como estimado»).
  if (q.dos_medidores && c.lecturas.B) return escritosSinReciboConDosMedidores(c) > 0 ? 'con dos medidores el PDF todavía no imprime meses estimados' : null;
  const aMano = periodosEscritosSinRecibo(c.lecturas.A, r.periodos);
  if (!aMano.length) return null;
  const formato = formatoPara(q, r.tarifa);
  if (q.consumo.es_nuevo) return null;
  const marcados = new Set(aMano);
  const estimados = r.periodos.filter((p, i) => p.origen === 'estimado' || marcados.has(i)).length;
  if (formato === 'baterias') return 'la propuesta de baterías solo imprime meses de recibo';
  const reales = r.periodos.length - estimados;
  const bimestral = r.facturacion === 'bimestral';
  // La misma cuenta que el PDF (mapear → «historial_incompleto»): en industria basta 1 mes real; si no, 3 meses o 2 bimestres.
  const sinTrabas = formato === 'industrial' || r.tarifa === 'GDMTO' || r.tarifa === 'GDMTH';
  const minimo = sinTrabas ? 1 : bimestral ? 2 : 3;
  if (reales < minimo)
    return `con ${reales === 0 ? 'ningún' : reales} ${bimestral ? (reales === 1 ? 'bimestre' : 'bimestres') : reales === 1 ? 'mes' : 'meses'} de recibo la propuesta no se apoya en el recibo (pide ${minimo === 1 ? 'al menos 1' : `al menos ${minimo}`}${bimestral ? ' bimestres' : ' meses'} con recibo)`;
  return null;
}

/** La nota con que queda confirmado el amarillo de la lectura cuando el vendedor ya completó el año. */
export const NOTA_HISTORIAL_COMPLETADO = 'Se completaron los periodos del año en Revisión: los que no tienen recibo los escribió el vendedor o los sugirió la IA (marcados en la tabla).';

/** hoy85 (verificador del vendedor): al AUTORIZAR una tabla a la que el vendedor ya le puso los meses que faltaban (z61), el amarillo de
 *  la lectura «El historial trae 8 de 12 periodos: los que faltan se estimarán» ya no es cierto y había que confirmarlo a mano para
 *  sacar el PDF. Se confirma solo, con la MISMA cuenta con que la lectura lo puso (paquetes/ia → revisiones: menos periodos que 12 o
 *  6) y a nombre de quien autoriza. El ROJO («solo hay 2 meses de consumo») NO: los meses de recibo siguen siendo 2 y eso lo ve
 *  quien lo tiene que ver. Lo usan el servidor y el servidor simulado. */
export function avisosConHistorialCompletado(c: ContenidoCotizacion, M: 'A' | 'B', usuarioId: string): ContenidoCotizacion {
  const l = c.lecturas[M];
  if (!l) return c;
  const necesarios = l.facturacion === 'bimestral' ? 6 : 12;
  if (l.historial.length < necesarios) return c;
  const campo = `/lecturas/${M}/historial`;
  let cambio = false;
  const avisos = c.avisos.map((a: Aviso) => {
    if (a.codigo !== 'HISTORIAL_INCOMPLETO' || a.origen !== 'ia_lectura' || a.nivel !== 'amarillo' || a.resuelto || a.campo !== campo) return a;
    cambio = true;
    return { ...a, resuelto: true, resuelto_por: usuarioId, nota: NOTA_HISTORIAL_COMPLETADO };
  });
  return cambio ? { ...c, avisos } : c;
}
